import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import EntityCard from "@/components/EntityCard";
import { Search, ArrowRight, Mountain, Sparkles, Shield, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LangContext";
import { useSeo } from "@/lib/useSeo";

const HERO_IMG = "https://images.unsplash.com/photo-1627894483216-2138af692e32?crop=entropy&cs=srgb&fm=jpg&q=85";

const CATEGORY_TABS = [
  { key: "dham", label: "Dhams" },
  { key: "yatra", label: "Yatras" },
  { key: "hotel", label: "Hotels" },
  { key: "package", label: "Packages" },
];

const TRUST_ITEMS = [
  { icon: Shield, t: "trust_1", s: "trust_1s" },
  { icon: HeartHandshake, t: "trust_2", s: "trust_2s" },
  { icon: Mountain, t: "trust_3", s: "trust_3s" },
];

export default function Home() {
  const { t, pick } = useLang();
  const [guides, setGuides] = useState([]);
  useSeo({ title: "IndiaDham — Religious Travel, Char Dham Yatra, Temple Hotels & Packages", description: "Discover Char Dham, Jyotirlingas, Shakti Peeths and legendary Yatras with trusted stays, curated packages and seamless bookings." });
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [packages, setPackages] = useState([]);
  const [tab, setTab] = useState("dham");
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/entities", { params: { featured: true, limit: 6 } }).then(r => setFeatured(r.data));
    api.get("/entities", { params: { type: "temple", limit: 4 } }).then(r => setPopular(r.data));
    api.get("/entities", { params: { type: "package", limit: 4 } }).then(r => setPackages(r.data));
    api.get("/guides").then(r => setGuides(r.data.slice(0, 4)));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    nav(q ? `/search?${params.toString()}` : `/${tab}s`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-indigo-deep/70 via-indigo-deep/50 to-indigo-deep/85" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-28 text-center text-offwhite">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full bg-gold/15 border border-gold/30 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            <span className="text-xs uppercase tracking-widest text-gold">{t("hero_badge")}</span>
          </div>
          <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-4xl mx-auto">
            {t("hero_title_1")} <span className="gold-gradient-text">{t("hero_title_2")}</span> {t("hero_title_3")}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-offwhite/80 max-w-2xl mx-auto">
            {t("hero_sub")}
          </p>

          {/* Search card */}
          <form onSubmit={onSearch} className="mt-8 max-w-3xl mx-auto bg-white rounded-2xl p-3 md:p-4 card-shadow">
            <div className="flex gap-1.5 mb-3 overflow-x-auto scrollbar-none">
              {CATEGORY_TABS.map(t => (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`hero-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium shrink-0 transition ${tab === t.key ? "bg-indigo-deep text-gold" : "text-indigo-deep/70 hover:bg-gold/10"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-indigo-deep/60" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={`Search ${CATEGORY_TABS.find(c => c.key === tab)?.label.toLowerCase() || "destinations"}, cities, states…`}
                  className="w-full h-11 pl-10 pr-3 rounded-lg border border-gold/30 text-sm text-indigo-deep focus:outline-none focus:ring-2 focus:ring-gold/50"
                />
              </div>
              <Button data-testid="hero-search-btn" type="submit" className="h-11 px-6 bg-gold hover:bg-gold-dark text-indigo-deep font-semibold">
                {t("explore")} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </form>

          {/* Trust bar */}
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-2xl mx-auto text-left">
            {TRUST_ITEMS.map((f) => (
              <div key={f.t} className="p-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10">
                <f.icon className="w-4 h-4 text-gold mb-1" />
                <div className="text-sm font-semibold text-white">{t(f.t)}</div>
                <div className="text-xs text-white/60">{t(f.s)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Char Dham featured */}
      <Section title={t("featured_title")} subtitle={t("featured_sub")} link="/dhams" viewAll={t("view_all")}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map(e => <EntityCard key={e.id} e={e} />)}
        </div>
      </Section>

      <Section title={t("temples_title")} subtitle={t("temples_sub")} link="/temples" viewAll={t("view_all")}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {popular.map(e => <EntityCard key={e.id} e={e} compact />)}
        </div>
      </Section>

      <Section title={t("packages_title")} subtitle={t("packages_sub")} link="/packages" viewAll={t("view_all")} alt>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {packages.map(e => <EntityCard key={e.id} e={e} compact />)}
        </div>
      </Section>

      <Section title={t("guides_title")} subtitle={t("guides_sub")} link="/guides" viewAll={t("view_all")}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="home-guides">
          {guides.map(g => (
            <Link key={g.id} to={`/guides/${g.slug}`} className="group relative rounded-xl overflow-hidden aspect-[4/5] bg-indigo-deep card-shadow">
              <img src={g.image} alt={pick(g, "title")} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-deep via-indigo-deep/30 to-transparent" />
              <div className="absolute bottom-0 p-4 text-white">
                <div className="text-[10px] uppercase tracking-widest text-gold">{g.state || g.dham}</div>
                <div className="font-display text-lg leading-snug mt-1">{pick(g, "title")}</div>
                <div className="text-xs text-white/70 mt-1 line-clamp-2">{pick(g, "best_time")}</div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <section className="bg-indigo-deep text-offwhite">
        <div className="max-w-5xl mx-auto px-4 py-12 md:py-16 text-center">
          <div className="spiritual-divider max-w-xs mx-auto mb-6" />
          <h2 className="font-display text-3xl md:text-4xl text-gold">{t("cta_title")}</h2>
          <p className="mt-3 text-offwhite/70 max-w-2xl mx-auto">{t("cta_sub")}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/yatras"><Button data-testid="cta-explore-yatras" className="bg-gold text-indigo-deep hover:bg-gold-dark hover:text-white h-11 px-6 font-semibold">{t("cta_explore")}</Button></Link>
            <a href="https://wa.me/919999999999?text=I%20want%20to%20plan%20a%20Yatra" target="_blank" rel="noreferrer">
              <Button data-testid="cta-whatsapp" variant="outline" className="border-gold text-gold hover:bg-gold hover:text-indigo-deep h-11 px-6">{t("cta_whatsapp")}</Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ title, subtitle, link, children, alt, viewAll }) {
  return (
    <section className={alt ? "bg-surfacealt/40" : ""}>
      <div className="max-w-7xl mx-auto px-4 py-10 md:py-14">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-indigo-deep">{title}</h2>
            {subtitle && <p className="text-sm md:text-base text-indigo-deep/60 mt-1">{subtitle}</p>}
          </div>
          {link && <Link to={link} className="text-sm font-semibold text-gold-dark hover:text-saffron whitespace-nowrap">{viewAll || "View all →"}</Link>}
        </div>
        {children}
      </div>
    </section>
  );
}
